import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { StateService } from '@uirouter/angular';

import * as _ from 'lodash-es';

import { ProjectService } from '../../../../services/project.service';
import { GfTranslateService } from '../../../../services/core.translate.service';
import { FilterService } from '../../../../services/core.filter.service';
import { BreadcrumbService } from '../../../../../widgets/breadcrumbs/breadcrumbs.service';
import { marker } from '../../../../services/ngx-translate-extract-marker';

@Component({
    selector: 'orgMembersAudit',
    template: require('./org.members-audit.component.html'),
    encapsulation: ViewEncapsulation.None,
    styles: [`
        .org-members-audit .sub-row {
            background-color: #f9f9f9;
        }
        .org-members-audit .sub-row td:first-child {
            border-left: 3px solid #337ab7;
        }
        .org-members-audit .nav-tabs > li > a {
            cursor: pointer;
        }
        .org-members-audit .table > tbody > tr.sub-row:hover {
            background-color: #f0f0f0;
        }
        .org-members-audit .label {
            font-size: 0.85em;
        }
        body.dark .org-members-audit .sub-row {
            background-color: #3a4756;
        }
        body.dark .org-members-audit .table > tbody > tr.sub-row:hover {
            background-color: #404c59;
        }
    `]
})
export class OrgMembersAuditComponent implements OnInit {

    activeTab: string = 'members';
    orgUnixName: string = '';
    orgName: string = '';
    isProjectContext: boolean = false;

    // Dynamic-filters data
    filters: any = {};
    sortFields: any = {};

    // Members tab
    members: any[] = [];
    membersTotalCount: number = 0;
    membersLoading: boolean = false;
    expandedUsers: { [userId: number]: boolean } = {};
    userProjects: { [userId: number]: any[] } = {};
    userProjectsLoading: { [userId: number]: boolean } = {};

    // Members filters (kept for backward compat with internal logic)
    searchText: string = '';
    filterRole: string = '';
    filterProject: string = '';
    filterMembershipType: string = '';
    filterActivity: string = '';

    // Members paging/sorting
    membersPageNum: number = 0;
    membersPageSize: number = 20;
    membersSortField: string = 'unixName';
    membersSortDir: string = 'asc';

    // Audit log tab
    auditItems: any[] = [];
    auditTotalCount: number = 0;
    auditLoading: boolean = false;

    // Audit filters
    auditUserFilter: string = '';
    auditDateStart: string = '';
    auditDateEnd: string = '';
    auditActionFilter: string = '';

    // Audit paging/sorting
    auditPageNum: number = 0;
    auditPageSize: number = 20;
    auditSortField: string = 'change_date';
    auditSortDir: string = 'desc';

    // Base filter definitions
    private membersBaseFilters: any[] = [];
    private auditBaseFilters: any[] = [];

    constructor(
        private projectService: ProjectService,
        public stateService: StateService,
        private gfTranslateService: GfTranslateService,
        private filterService: FilterService,
        private breadcrumbService: BreadcrumbService,
    ) { }

    /** Initializes component state from route params, loads org data and members. */
    ngOnInit() {
        this.orgUnixName = _.get(this.stateService.params, 'unixName', '') || _.get(this.stateService.params, 'selectedProject', '');
        this.isProjectContext = !!_.get(this.stateService.params, 'unixName', '');
        this.activeTab = _.get(this.stateService.params, 'tab', 'members') || 'members';

        // Read paging from state params
        this.membersPageNum = parseInt(_.get(this.stateService.params, 'page_num', '0'), 10) || 0;
        this.membersPageSize = parseInt(_.get(this.stateService.params, 'page_size', '20'), 10) || 20;
        this.membersSortField = _.get(this.stateService.params, 'sort_field', 'unixName') || 'unixName';
        this.membersSortDir = _.get(this.stateService.params, 'sort_dir', 'asc') || 'asc';

        // Read filters from state params
        this.searchText = _.get(this.stateService.params, 'search', '') || '';
        this.filterRole = _.get(this.stateService.params, 'role', '') || '';
        this.filterProject = _.get(this.stateService.params, 'project', '') || '';
        this.filterMembershipType = _.get(this.stateService.params, 'membership_type', '') || '';
        this.filterActivity = _.get(this.stateService.params, 'activity', '') || '';

        this.initFilters();

        if (this.orgUnixName) {
            this.loadOrgName();
            this.loadFilterOptions();
            this.loadMembers();
            if (this.activeTab === 'audit') {
                this.loadAuditLog();
            }
        }
    }

    /** Builds the base filter and sort definitions for members and audit tabs. */
    initFilters() {
        const all = { id: '', label: 'All', defaultSelected: true };
        const any = { id: '', label: 'Any', defaultSelected: true };

        this.membersBaseFilters = [
            { alias: 'search', fieldName: 'Search', value: this.searchText || '', fieldType: 4, isExtraField: false },
            {
                alias: 'role', fieldName: 'Role', value: all, fieldType: 1, isExtraField: false,
                fieldElements: [all]
            },
            {
                alias: 'project', fieldName: 'Project', value: all, fieldType: 1, isExtraField: false,
                fieldElements: [all]
            },
            {
                alias: 'membership_type', fieldName: 'Type', value: all, fieldType: 1, isExtraField: false,
                fieldElements: [
                    all,
                    { id: 'direct', label: 'Direct' },
                    { id: 'group', label: 'Group' }
                ]
            },
            {
                alias: 'activity', fieldName: 'Activity', value: all, fieldType: 1, isExtraField: false,
                fieldElements: [
                    all,
                    { id: 'inactive_1_month', label: 'Inactive for 1+ month' },
                    { id: 'inactive_3_months', label: 'Inactive for 3+ months' },
                    { id: 'inactive_6_months', label: 'Inactive for 6+ months' },
                    { id: 'inactive_1_year', label: 'Inactive for 1+ year' },
                    { id: 'active_1_month', label: 'Active in last month' },
                    { id: 'active_3_months', label: 'Active in last 3 months' },
                    { id: 'active_6_months', label: 'Active in last 6 months' },
                    { id: 'active_1_year', label: 'Active in last year' }
                ]
            }
        ];

        this.auditBaseFilters = [
            { alias: 'user', fieldName: 'User Affected', value: this.auditUserFilter || '', fieldType: 4, isExtraField: false },
            { alias: 'dateStart', fieldName: 'From Date', value: this.auditDateStart || '', fieldType: 9, isExtraField: false },
            { alias: 'dateEnd', fieldName: 'To Date', value: this.auditDateEnd || '', fieldType: 9, isExtraField: false },
            {
                alias: 'action', fieldName: 'Action', value: any, fieldType: 1, isExtraField: false,
                fieldElements: [
                    any,
                    { id: '_created_', label: 'Added' },
                    { id: '_deleted_', label: 'Removed' },
                    { id: 'project_group_member', label: 'Member Changed' }
                ]
            }
        ];

        // Set filter values from state params
        this.membersBaseFilters = this.filterService.setFilterValues(this.membersBaseFilters, this.stateService.params);
        this.auditBaseFilters = this.filterService.setFilterValues(this.auditBaseFilters, this.stateService.params);

        this.buildFilters();
        this.buildSortFields();
    }

    /** Assigns the active tab's filter array to the shared `filters` object for the template. */
    buildFilters() {
        if (this.activeTab === 'members') {
            this.filters = {
                'member-filters': this.membersBaseFilters
            };
        } else {
            this.filters = {
                'audit-filters': this.auditBaseFilters
            };
        }
    }

    /** Assigns the active tab's sortable column map to the shared `sortFields` object. */
    buildSortFields() {
        if (this.activeTab === 'members') {
            this.sortFields = {
                'unixName': 'User',
                'last_access': 'Last Access',
                'project_count': 'Projects'
            };
        } else {
            this.sortFields = {
                'change_date': 'Date',
                'section': 'Section',
                'id': 'ID'
            };
        }
    }

    /** Fetches the org project to populate `orgName` and breadcrumbs. */
    loadOrgName() {
        this.projectService.get(this.orgUnixName)
            .then((data: any) => {
                this.orgName = data.name || this.orgUnixName;
                this.setBreadcrumbs(data);
            }, () => {
                this.orgName = this.orgUnixName;
                this.setBreadcrumbs(null);
            });
    }

    /**
     * Sets the breadcrumb trail for the current org context.
     * @param project - The project object, or null to use org name as fallback.
     */
    private setBreadcrumbs(project: any) {
        const crumbs = [
            this.breadcrumbService.projectCrumb(project || { name: this.orgName, unixName: this.orgUnixName }),
            this.breadcrumbService.translatedCrumb(marker('Org Members Audit'), '.', {}),
        ];
        this.breadcrumbService.setCrumbs(crumbs);
    }

    /** Loads distinct projects and roles from the server to populate filter dropdowns. */
    loadFilterOptions() {
        this.projectService.orgProjectsAndRoles(this.orgUnixName)
            .then((resp: any) => {
                const data = resp.items || resp;
                const all = { id: '', label: 'All', defaultSelected: true };

                // Populate project dropdown
                const projectFilter = _.find(this.membersBaseFilters, { alias: 'project' });
                if (projectFilter && data.projects) {
                    projectFilter.fieldElements = _.concat(
                        [all],
                        _.map(data.projects, (p) => ({ id: p.unix_name, label: p.project_name }))
                    );
                    projectFilter.value = this.filterService.setFilterValues([projectFilter], this.stateService.params)[0].value;
                }

                // Populate role dropdown
                const roleFilter = _.find(this.membersBaseFilters, { alias: 'role' });
                if (roleFilter && data.roles) {
                    roleFilter.fieldElements = _.concat(
                        [all],
                        _.map(data.roles, (r) => ({ id: r.role_name, label: r.role_name }))
                    );
                    roleFilter.value = this.filterService.setFilterValues([roleFilter], this.stateService.params)[0].value;
                }

                this.buildFilters();
            }, () => {
                // Filter options failed to load — keep text-level defaults
            });
    }

    // --- Filter callbacks for <dynamic-filters> ---

    /** Callback for <dynamic-filters> apply; extracts filter values, resets page, and reloads. */
    onApplyFilters = () => {
        if (this.activeTab === 'members') {
            this.extractMembersFilters();
            this.membersPageNum = 0;
            this.updateStateParams();
            this.loadMembers();
        } else {
            this.extractAuditFilters();
            this.auditPageNum = 0;
            this.loadAuditLog();
        }
    }

    /** Callback for <dynamic-filters> reset; clears all filter values and reloads. */
    onResetFilters = () => {
        if (this.activeTab === 'members') {
            this.searchText = '';
            this.filterRole = '';
            this.filterProject = '';
            this.filterMembershipType = '';
            this.filterActivity = '';
            this.membersBaseFilters = this.resetFilterValues(this.membersBaseFilters);
            this.buildFilters();
            this.membersPageNum = 0;
            this.updateStateParams();
            this.loadMembers();
        } else {
            this.auditUserFilter = '';
            this.auditDateStart = '';
            this.auditDateEnd = '';
            this.auditActionFilter = '';
            this.auditBaseFilters = this.resetFilterValues(this.auditBaseFilters);
            this.buildFilters();
            this.auditPageNum = 0;
            this.loadAuditLog();
        }
    }

    /**
     * Callback for <dynamic-filters> sort; updates sort field/direction and reloads.
     * @param params - Contains `key` (field name) and `sortDir` ('asc'|'desc').
     */
    onSort = (params: any) => {
        const field = _.get(params, 'key', '');
        const dir = _.get(params, 'sortDir', 'asc');
        if (this.activeTab === 'members') {
            this.membersSortField = field || 'unixName';
            this.membersSortDir = dir;
            this.membersPageNum = 0;
            this.updateStateParams();
            this.loadMembers();
        } else {
            this.auditSortField = field || 'change_date';
            this.auditSortDir = dir;
            this.auditPageNum = 0;
            this.loadAuditLog();
        }
    }

    // --- Members Tab ---

    /** Fetches the paginated, flat member listing from the server. Clears cached project details. */
    loadMembers() {
        this.membersLoading = true;
        this.userProjects = {};
        this.userProjectsLoading = {};
        const params: any = {
            page_num: this.membersPageNum,
            page_size: this.membersPageSize,
            sort_field: this.membersSortField,
            sort_dir: this.membersSortDir,
        };

        if (this.searchText) { params.search = this.searchText; }
        if (this.filterRole) { params.role = this.filterRole; }
        if (this.filterProject) { params.project = this.filterProject; }
        if (this.filterMembershipType) { params.membership_type = this.filterMembershipType; }
        if (this.filterActivity) { params.activity = this.filterActivity; }

        this.projectService.orgMembers(this.orgUnixName, params)
            .then((data: any) => {
                this.members = data.items || [];
                this.membersTotalCount = (data.meta && data.meta.total_count) || 0;
                this.membersLoading = false;
            }, () => {
                this.members = [];
                this.membersTotalCount = 0;
                this.membersLoading = false;
            });
    }

    /** Resets to page 0, syncs state params, and reloads the members listing. */
    applyMembersFilters() {
        this.membersPageNum = 0;
        this.updateStateParams();
        this.loadMembers();
    }

    /** Clears all member filter values and reloads. */
    resetMembersFilters() {
        this.searchText = '';
        this.filterRole = '';
        this.filterProject = '';
        this.filterMembershipType = '';
        this.applyMembersFilters();
    }

    /**
     * Toggles sort direction if the same field is clicked, otherwise sorts ascending by new field.
     * @param field - The column key to sort by.
     */
    sortMembers(field: string) {
        if (this.membersSortField === field) {
            this.membersSortDir = this.membersSortDir === 'asc' ? 'desc' : 'asc';
        } else {
            this.membersSortField = field;
            this.membersSortDir = 'asc';
        }
        this.membersPageNum = 0;
        this.updateStateParams();
        this.loadMembers();
    }

    /**
     * Pager callback for the members tab; updates page/size and reloads.
     * @param event - Contains `page_num` and `page_size`.
     */
    membersPagerCallback = (event: any) => {
        this.membersPageNum = _.get(event, 'page_num', 0);
        this.membersPageSize = _.get(event, 'page_size', 20);
        this.updateStateParams();
        this.loadMembers();
    }

    /**
     * Toggles the expanded state for a user row. Lazy-loads project details on first expand.
     * @param userId - The user whose row to expand/collapse.
     */
    toggleUserExpand(userId: number) {
        this.expandedUsers[userId] = !this.expandedUsers[userId];
        if (this.expandedUsers[userId] && !this.userProjects[userId]) {
            this.loadUserProjects(userId);
        }
    }

    /**
     * Fetches project/role detail for a single user from the orgMemberProjects endpoint.
     * @param userId - The user whose projects to load.
     */
    loadUserProjects(userId: number) {
        this.userProjectsLoading[userId] = true;
        this.projectService.orgMemberProjects(this.orgUnixName, userId)
            .then((data: any) => {
                this.userProjects[userId] = data.items || data;
                this.userProjectsLoading[userId] = false;
            }, () => {
                this.userProjects[userId] = [];
                this.userProjectsLoading[userId] = false;
            });
    }

    /**
     * Returns whether the given user's project detail row is currently expanded.
     * @param userId - The user to check.
     */
    isExpanded(userId: number): boolean {
        return !!this.expandedUsers[userId];
    }

    /**
     * Returns the Bootstrap glyphicon class for the sort indicator on the given column.
     * @param field - The column key to check.
     */
    getSortIcon(field: string): string {
        if (this.membersSortField !== field) { return 'glyphicon-sort'; }
        return this.membersSortDir === 'asc' ? 'glyphicon-sort-by-alphabet' : 'glyphicon-sort-by-alphabet-alt';
    }

    get membersCsvUrl(): string {
        const params: any = {};
        if (this.searchText) { params.search = this.searchText; }
        if (this.filterRole) { params.role = this.filterRole; }
        if (this.filterProject) { params.project = this.filterProject; }
        if (this.filterMembershipType) { params.membership_type = this.filterMembershipType; }
        if (this.filterActivity) { params.activity = this.filterActivity; }
        return this.projectService.orgMembersCsvUrl(this.orgUnixName, params);
    }

    // --- Audit Log Tab ---

    /**
     * Switches between members and audit tabs, rebuilds filters/sorts, and lazy-loads audit data.
     * @param tab - 'members' or 'audit'.
     */
    switchTab(tab: string) {
        this.activeTab = tab;
        this.buildFilters();
        this.buildSortFields();
        this.stateService.go('.', { tab: tab }, { notify: false });
        if (tab === 'audit' && this.auditItems.length === 0) {
            this.loadAuditLog();
        }
    }

    /** Fetches the paginated audit log from the server using current filters and sorting. */
    loadAuditLog() {
        this.auditLoading = true;
        const params: any = {
            page_num: this.auditPageNum,
            page_size: this.auditPageSize,
            sort_field: this.auditSortField,
            sort_dir: this.auditSortDir,
        };

        if (this.auditUserFilter) { params.user = this.auditUserFilter; }
        if (this.auditDateStart) { params.dateStart = this.auditDateStart; }
        if (this.auditDateEnd) { params.dateEnd = this.auditDateEnd; }
        if (this.auditActionFilter) { params.action = this.auditActionFilter; }

        this.projectService.membershipAudit(this.orgUnixName, params)
            .then((data: any) => {
                this.auditItems = data.items || [];
                this.auditTotalCount = (data.meta && data.meta.total_count) || 0;
                this.auditLoading = false;
            }, () => {
                this.auditItems = [];
                this.auditTotalCount = 0;
                this.auditLoading = false;
            });
    }

    /** Resets to page 0 and reloads the audit log. */
    applyAuditFilters() {
        this.auditPageNum = 0;
        this.loadAuditLog();
    }

    /** Clears all audit filter values and reloads. */
    resetAuditFilters() {
        this.auditUserFilter = '';
        this.auditDateStart = '';
        this.auditDateEnd = '';
        this.auditActionFilter = '';
        this.applyAuditFilters();
    }

    /**
     * Pager callback for the audit tab; updates page/size and reloads.
     * @param event - Contains `page_num` and `page_size`.
     */
    auditPagerCallback = (event: any) => {
        this.auditPageNum = _.get(event, 'page_num', 0);
        this.auditPageSize = _.get(event, 'page_size', 20);
        this.loadAuditLog();
    }

    get auditCsvUrl(): string {
        const params: any = {};
        if (this.auditUserFilter) { params.user = this.auditUserFilter; }
        if (this.auditDateStart) { params.dateStart = this.auditDateStart; }
        if (this.auditDateEnd) { params.dateEnd = this.auditDateEnd; }
        if (this.auditActionFilter) { params.action = this.auditActionFilter; }
        return this.projectService.membershipAuditCsvUrl(this.orgUnixName, params);
    }

    /** Navigates back to the project summary or admin orgs page depending on context. */
    goBack() {
        if (this.isProjectContext) {
            this.stateService.go('home.project.summary', { unixName: this.orgUnixName });
        } else {
            this.stateService.go('home.admin.orgs', { selectedProject: this.orgUnixName });
        }
    }

    // --- Helpers ---

    /** Reads current filter widget values into the component's members filter properties. */
    private extractMembersFilters() {
        const vals = this.filterService.getFilterValues(this.membersBaseFilters);
        this.searchText = vals['search'] || '';
        this.filterRole = vals['role'] || '';
        this.filterProject = vals['project'] || '';
        this.filterMembershipType = vals['membership_type'] || '';
        this.filterActivity = vals['activity'] || '';
    }

    /** Reads current filter widget values into the component's audit filter properties. */
    private extractAuditFilters() {
        const vals = this.filterService.getFilterValues(this.auditBaseFilters);
        this.auditUserFilter = vals['user'] || '';
        this.auditDateStart = vals['dateStart'] || '';
        this.auditDateEnd = vals['dateEnd'] || '';
        this.auditActionFilter = vals['action'] || '';
    }

    /**
     * Resets each filter to its default value (first `defaultSelected` element or empty string).
     * @param filters - The filter array to reset.
     * @returns A new array with reset filter values.
     */
    private resetFilterValues(filters: any[]): any[] {
        return _.map(filters, (f) => {
            if (f.fieldType === 1 && f.fieldElements) {
                f.value = _.find(f.fieldElements, { defaultSelected: true }) || { id: '', label: 'Any' };
            } else {
                f.value = '';
            }
            return f;
        });
    }

    /** Syncs current member filters, pagination, and sorting into the URL state params without triggering a reload. */
    private updateStateParams() {
        this.stateService.go('.', {
            page_num: this.membersPageNum,
            page_size: this.membersPageSize,
            sort_field: this.membersSortField,
            sort_dir: this.membersSortDir,
            search: this.searchText || null,
            role: this.filterRole || null,
            project: this.filterProject || null,
            membership_type: this.filterMembershipType || null,
            activity: this.filterActivity || null,
            tab: this.activeTab,
        }, { notify: false });
    }
}
